import React from 'react';
import { 
  CheckSquare, Heart, X, Plus, Eye, ThumbsUp, ThumbsDown, 
  ShoppingCart, BookOpen, Star
} from 'lucide-react';

// Category-specific engagement options
const getEngagementOptions = (category) => {
  switch (category) {
    case 'project':
      return [
        { id: 'add_to_tasks', icon: CheckSquare, label: 'Add to Tasks', color: 'text-azure-600 hover:bg-azure-50' },
        { id: 'like', icon: Heart, label: 'Like', color: 'text-red-500 hover:bg-red-50' },
        { id: 'not_interested', icon: X, label: 'Not Interested', color: 'text-stone-500 hover:bg-stone-50' }
      ];
    
    case 'recipe':
      return [
        { id: 'add_to_tasks', icon: CheckSquare, label: 'Add to Tasks', color: 'text-azure-600 hover:bg-azure-50' },
        { id: 'like', icon: Heart, label: 'Like', color: 'text-red-500 hover:bg-red-50' },
        { id: 'not_interested', icon: X, label: 'Not Interested', color: 'text-stone-500 hover:bg-stone-50' }
      ];
    
    case 'learning':
      return [
        { id: 'add_to_tasks', icon: CheckSquare, label: 'Add to Tasks', color: 'text-azure-600 hover:bg-azure-50' },
        { id: 'like', icon: Heart, label: 'Like', color: 'text-red-500 hover:bg-red-50' },
        { id: 'not_interested', icon: X, label: 'Not Interested', color: 'text-stone-500 hover:bg-stone-50' }
      ];
    
    case 'movie':
      return [
        { id: 'to_watch', icon: Eye, label: 'To Watch', color: 'text-violet-600 hover:bg-violet-50' },
        { id: 'watched_liked', icon: ThumbsUp, label: 'Liked', color: 'text-emerald-600 hover:bg-emerald-50' },
        { id: 'watched_disliked', icon: ThumbsDown, label: 'Disliked', color: 'text-orange-600 hover:bg-orange-50' },
        { id: 'not_interested', icon: X, label: 'Not Interested', color: 'text-stone-500 hover:bg-stone-50' }
      ];
    
    case 'book':
      return [
        { id: 'to_read', icon: BookOpen, label: 'To Read', color: 'text-emerald-600 hover:bg-emerald-50' },
        { id: 'to_buy', icon: ShoppingCart, label: 'To Buy', color: 'text-azure-600 hover:bg-azure-50' },
        { id: 'finished', icon: Star, label: 'Finished', color: 'text-yellow-600 hover:bg-yellow-50' },
        { id: 'not_interested', icon: X, label: 'Not Interested', color: 'text-stone-500 hover:bg-stone-50' }
      ];
    
    case 'experience':
      return [
        { id: 'add_to_tasks', icon: CheckSquare, label: 'Add to Tasks', color: 'text-azure-600 hover:bg-azure-50' },
        { id: 'like', icon: Heart, label: 'Like', color: 'text-red-500 hover:bg-red-50' },
        { id: 'not_interested', icon: X, label: 'Not Interested', color: 'text-stone-500 hover:bg-stone-50' }
      ];
    
    default:
      return [
        { id: 'like', icon: Heart, label: 'Like', color: 'text-red-500 hover:bg-red-50' },
        { id: 'not_interested', icon: X, label: 'Not Interested', color: 'text-stone-500 hover:bg-stone-50' }
      ];
  }
};

export default function EngagementActions({ 
  category, 
  onAction, 
  compact = false,
  currentStatus = null 
}) {
  const options = getEngagementOptions(category);

  const handleAction = (actionId) => {
    if (onAction) {
      onAction(actionId, category);
    }
  };

  if (compact) {
    // Compact view for integrated bottom bar - clean horizontal layout
    return (
      <div className="flex items-center justify-center gap-4">
        {options.map((option) => {
          const Icon = option.icon;
          const isActive = currentStatus === option.id;
          
          return (
            <button
              key={option.id}
              onClick={(e) => {
                e.stopPropagation();
                handleAction(option.id);
              }}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
                isActive 
                  ? 'bg-teal-600 text-white shadow-sm scale-110' 
                  : 'text-teal-600 hover:text-teal-700 hover:bg-teal-100 hover:shadow-sm'
              }`}
              title={option.label}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}
      </div>
    );
  }

  // Full view for detail modals - vertical layout with icons and text
  return (
    <div className="grid grid-cols-2 gap-2 mt-4">
      {options.map((option) => {
        const Icon = option.icon;
        const isActive = currentStatus === option.id;
        
        return (
          <button
            key={option.id}
            onClick={() => handleAction(option.id)}
            className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
              isActive 
                ? `${option.color} border-current bg-current bg-opacity-10` 
                : `text-slate-600 border-stone-200 hover:border-stone-300 ${option.color}`
            }`}
          >
            <Icon className="w-4 h-4" />
            <span className="text-sm font-medium">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Helper function to get status display info
export const getStatusInfo = (category, status) => {
  const options = getEngagementOptions(category);
  const option = options.find(opt => opt.id === status);
  
  if (!option) return null;
  
  return {
    icon: option.icon,
    label: option.label,
    color: option.color
  };
};